import { useApolloClient } from '@apollo/client'
import * as Sentry from '@sentry/browser'
import { useEffect } from 'react'

import consumer from '../../cable'
import {
  AiChatConversationDocument,
  AiChatConversationQuery,
  AiChatConversationQueryVariables
} from '../../graphql/queries/aiChat/__generated__/aiChatConversationQuery.generated'
import type { ChatMessageRole } from '../../stores/chatStore'
import { useChatStore } from '../../stores/chatStore'
import { getClient, getToken } from '../helper'
import { useUser } from '../hooks'

// I2: Bleibt ein Turn (status: 'waiting') länger als diese Zeit offen (z.B.
// verlorener Callback, Hermine down ohne dass der Job-Rescue greift), zwingt
// der Watchdog den Fehlerzustand, statt die UI dauerhaft im Loading-State
// hängen zu lassen.
const WATCHDOG_TIMEOUT_MS = 5 * 60 * 1000

/**
 * Langlebige ActionCable-Subscription für eine AiChatConversation (Muster:
 * useHermineRequestSubscription). Anders als dort ist die Subscription nicht
 * an eine einzelne Anfrage gebunden, sondern lebt für die Dauer der
 * Konversation (solange chatStore.conversationId gesetzt ist) und dispatcht
 * eingehende Broadcasts direkt in den chatStore.
 *
 * Broadcast-Kontrakt (AiChatChannel):
 * - {type: 'message', message: {id, role, content, status, created_at}}
 * - {type: 'done', usage: {...}}
 * - {type: 'error', code: 'ai_chat.turn_failed' | 'ai_chat.turn_timeout'}
 * Unbekannte type-Werte (z.B. spätere Tool-Events) werden bewusst ignoriert.
 */
const useAiChatChannel = () => {
  const { user } = useUser()
  const apolloClient = useApolloClient()
  const conversationId = useChatStore(state => state.conversationId)
  const status = useChatStore(state => state.status)
  const addMessage = useChatStore(state => state.addMessage)
  const setStatus = useChatStore(state => state.setStatus)

  // I2: Watchdog für hängende Turns. Der Timer läuft nur, solange status
  // 'waiting' ist — jeder Statuswechsel (z.B. 'idle' durch ein 'done'- oder
  // 'error' durch ein 'error'-Broadcast/rejected) räumt ihn über den
  // Effect-Cleanup wieder auf, weil `status` dann nicht mehr 'waiting' ist.
  useEffect(() => {
    if (status !== 'waiting') return undefined

    const timeoutId = window.setTimeout(() => setStatus('error'), WATCHDOG_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [status, setStatus])

  useEffect(() => {
    if (!conversationId) return undefined

    // I6: Token/Client werden bewusst bei jedem (Re-)Subscribe frisch
    // gelesen (dieser Effect läuft neu, sobald sich conversationId oder
    // user?.id ändert) statt einmal außerhalb des Effects gecacht zu werden.
    // Volle Reaktivität auf eine Token-Rotation *während* eine Subscription
    // bereits steht, ist NICHT abgedeckt (ActionCable behält den einmal
    // übergebenen Identifier bis zum nächsten Resubscribe) — siehe Report.
    const token = getToken()
    const client = getClient()
    const params = {
      channel: 'AiChatChannel',
      user_id: `${user?.id}`,
      conversation_id: conversationId,
      token,
      client
    }

    // I2: Erster-Turn-Härtung — bei jedem (Re-)Connect (initialer Subscribe,
    // Reconnect nach Netzwerkaussetzer) die Konversation per network-only
    // resynchronisieren. Fängt Broadcasts ab, die während einer
    // Verbindungslücke verloren gingen (z.B. der allererste Turn, dessen
    // Antwort kommt, bevor die Subscription überhaupt vollständig steht).
    // addMessage dedupliziert per Message-Id, ein erneuter Fetch legt also
    // keine Duplikate an.
    const resyncConversation = () => {
      apolloClient
        .query<AiChatConversationQuery, AiChatConversationQueryVariables>({
          query: AiChatConversationDocument,
          variables: { id: conversationId },
          fetchPolicy: 'network-only'
        })
        .then(({ data }) => {
          const messages = data.aiChatConversation?.messages ?? []

          messages.forEach(message => {
            if (!message?.id) return
            addMessage({
              id: message.id,
              role: (message.role as ChatMessageRole) ?? 'assistant',
              content: message.content ?? '',
              status: message.status,
              createdAt: message.createdAt ?? undefined,
              uploads: (message.uploads ?? []).map(upload => ({
                filename: upload.attachmentFilename ?? '',
                url: upload.attachmentUrl
              }))
            })
          })

          const lastAssistantMessage = messages
            .filter(message => message.role === 'assistant')
            .at(-1)
          if (lastAssistantMessage?.status === 'completed') {
            setStatus('idle')
          } else if (lastAssistantMessage?.status === 'failed') {
            setStatus('error')
          }
        })
        .catch(error => {
          console.debug('[!] ActionCable - AiChatChannel - resync failed', error)
          Sentry.captureException(error)
        })
    }

    console.debug('[*] creating ai chat conversation subscription')
    consumer.subscriptions.create(params, {
      connected: () => {
        console.debug('[+] ActionCable - AiChatChannel - CONNECTED')
        resyncConversation()
      },
      disconnected: () => {
        console.debug('[-] ActionCable - AiChatChannel - DISCONNECTED')
      },
      rejected: () => {
        // I6: Server hat die Subscription abgelehnt (z.B. invalider Token,
        // fremde/gelöschte Konversation, siehe AiChatChannel#subscribed).
        // Kein Retry-Loop im MVP — nur ein sichtbarer Fehlerzustand statt
        // eines endlos hängenden 'waiting'.
        console.debug('[!] ActionCable - AiChatChannel - REJECTED')
        setStatus('error')
      },
      received: (data: any) => {
        console.debug('[+] ActionCable - AiChatChannel - received data: ', data)

        switch (data?.type) {
          case 'message': {
            const message = data.message || {}
            addMessage({
              id: `${message.id}`,
              role: message.role,
              content: message.content,
              status: message.status,
              createdAt: message.created_at
            })
            break
          }
          case 'done': {
            // status wird bewusst erst hier auf 'idle' gesetzt, nicht schon
            // beim 'message'-Event (siehe Task-Brief).
            setStatus('idle')
            window.dispatchEvent(new CustomEvent('m2:ai-chat-done'))
            break
          }
          case 'error': {
            setStatus('error')
            Sentry.captureMessage('AiChatChannel: turn failed.', {
              extra: data,
              level: 'warning'
            })
            break
          }
          default:
            // Unbekannte/zukünftige Event-Typen (z.B. Tool-Events) tolerant ignorieren.
            break
        }
      }
    })

    return () => {
      console.debug('[-] Unmounting AiChatChannel subscription')
      const sub = consumer.subscriptions.subscriptions?.find(
        (s: any) => s.identifier === JSON.stringify(params)
      )

      sub?.unsubscribe()
    }
  }, [conversationId, user?.id, apolloClient, addMessage, setStatus])
}

export default useAiChatChannel
